import React, { useState, useMemo } from 'react';
import { IRecipeStepEditable } from '../../types/recipes-types';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ElementSize } from '../../types/element-size';
import IconBin from '../Icons/IconBin/IconBin';
import IconEditOutlined from '../Icons/IconEditOutlined/IconEditOutlined';
import IconLeftArrow from '../Icons/IconLeftArrow/IconLeftArrow';
import Modal from '../Modal/Modal';
import ModalFullScreen from '../ModalFullScreen/ModalFullScreen';
import { RecipeStepForm } from '../../pages/Recipes/Components/RecipeStepForm';
import {
  initialStepValues,
  validationStepSchema,
  stepsLimitCount,
  undefinedIndex,
} from '../../pages/Recipes/constants';
import { generateStepName } from '../../pages/Recipes/utils';
import CycleMeasurement from '../../enums/cycle-measurement';
import Table from '../Table/Table';
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';
import Tooltip from '../Tooltip';

type StepsInputTableProps = {
  steps: IRecipeStepEditable[];
  onChange: (steps: IRecipeStepEditable[]) => void;
  contractTemperatureUnit?: 'Celsius' | 'Fahrenheit';
};

const StepsInputTable: React.FC<StepsInputTableProps> = ({ steps, onChange, contractTemperatureUnit = 'Celsius' }) => {
  const [editingIndex, setEditingIndex] = useState<number>(undefinedIndex);
  const [deletingIndex, setDeletingIndex] = useState<number>(undefinedIndex);
  const [isCreating, setIsCreating] = useState(false);

  const handleAddStep = (newStep: IRecipeStepEditable) => {
    const step = { ...newStep, order: steps.length };
    onChange([...steps, step]);
    setIsCreating(false);
  };

  const handleEditStep = (updatedStep: IRecipeStepEditable) => {
    const newSteps = steps.map((step, idx) => (idx === editingIndex ? updatedStep : step));
    onChange(newSteps);
    setEditingIndex(undefinedIndex);
  };

  const handleRemoveStep = () => {
    const newSteps = steps.filter((_, idx) => idx !== deletingIndex).map((step, idx) => ({ ...step, order: idx }));
    onChange(newSteps);
    setDeletingIndex(undefinedIndex);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = { ...newSteps[index - 1], order: index };
    newSteps[index - 1] = { ...temp, order: index - 1 };
    onChange(newSteps);
  };

  const handleMoveDown = (index: number) => {
    if (index === steps.length - 1) return;
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = { ...newSteps[index + 1], order: index };
    newSteps[index + 1] = { ...temp, order: index + 1 };
    onChange(newSteps);
  };

  const viewValues = steps.map((item, idx) => ({
    Name: `${idx + 1}. ${generateStepName(item)}`,
    id: `${idx}`,
    idx,
    order: item.order,
  }));

  const isDisabledBtn = useMemo(() => {
    return steps.length >= stepsLimitCount;
  }, [steps]);

  const editStepTitle = useMemo(() => {
    if (editingIndex < 0) {
      return '';
    }
    return `Edit Recipe Step #${steps[editingIndex].order + 1}`;
  }, [editingIndex, steps]);

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint="Edit">
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => setEditingIndex(payload.idx),
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => (
        <Tooltip hint="Delete">
          <IconBin fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => setDeletingIndex(payload.idx),
    },
    {
      icon: () => (
        <Tooltip hint="Move Step Earlier" hintClassName="right-[-220%] bottom-7" isCustomSettings>
          <div style={{ transform: 'rotate(90deg)' }}>
            <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
          </div>
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => handleMoveUp(payload.idx),
    },
    {
      icon: () => (
        <Tooltip hint="Move Step Later" hintClassName="right-[-40%] bottom-7" isCustomSettings>
          <div style={{ transform: 'rotate(-90deg)' }}>
            <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
          </div>
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => handleMoveDown(payload.idx),
    },
  ];

  return (
    <>
      <div className="flex w-full flex-col gap-5">
        <div className="flex h-10 w-full flex-row items-center justify-between">
          <h2 className="text-title-md2 font-semibold text-black dark:text-white">Recipe Steps</h2>
        </div>
        <div>
          <Table values={viewValues} defaultKeys={['Name']} actionsButtons={actionsButtons} />
        </div>
        <div className="flex w-full justify-start">
          <InteractiveButton text="Create Step" disabled={isDisabledBtn} onClick={() => setIsCreating(true)} />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        modalTitle="Recipe Step Deletion"
        isVisible={deletingIndex >= 0}
        content={`Are you sure you want to delete the '${
          deletingIndex >= 0 && generateStepName(steps[deletingIndex])
        }' step?`}
        acceptBtnText="Delete"
        onClose={() => setDeletingIndex(undefinedIndex)}
        onAccept={handleRemoveStep}
        showAcceptBtn
        showCancelBtn
      />

      {/* Create Step Modal */}
      <ModalFullScreen
        isVisible={isCreating}
        modalTitle="Create Step"
        onClose={() => setIsCreating(false)}
        className="sm:max-h-fit sm:max-w-100 sm:rounded-lg lg:max-w-142.5 lg:px-17.5 lg:py-15"
      >
        <RecipeStepForm
          initialValues={initialStepValues}
          validationSchema={validationStepSchema}
          onSubmit={handleAddStep}
          submitButtonName="Create"
          prefferedUnits={
            contractTemperatureUnit === 'Celsius' ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit
          }
          disableUnitsSwitcher={true}
        />
      </ModalFullScreen>

      {/* Edit Step Modal */}
      <ModalFullScreen
        isVisible={editingIndex >= 0}
        modalTitle={editStepTitle}
        onClose={() => setEditingIndex(undefinedIndex)}
        className="sm:max-h-fit sm:max-w-100 sm:rounded-lg lg:max-w-142.5 lg:px-17.5 lg:py-15"
      >
        {editingIndex >= 0 && (
          <RecipeStepForm
            initialValues={steps[editingIndex]}
            validationSchema={validationStepSchema}
            onSubmit={handleEditStep}
            submitButtonName="Update"
            prefferedUnits={
              contractTemperatureUnit === 'Celsius' ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit
            }
            disableUnitsSwitcher={true}
          />
        )}
      </ModalFullScreen>
    </>
  );
};

export default StepsInputTable;
